import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {type ApiResponseGetMonthlyDrivingInfoResponse, ApiResponseGetTripDaysResponse, Trip} from '@di-shared/api/di/__generated';
import {
  endDateOfMonth,
  isDateSameWeek,
  isSameYearAndMonth,
  parseDateWithFormat,
  setDay,
  startDateOfMonth,
  subDate,
  weekOfInterval,
} from '@shared/dateUtils';
import {VALID_MONTH_PERIOD} from '@di-shared/features/history/driving-info/useDrivingInfo.kr';

function getFocusTripDate(ccsAgreeDate: Date, selectedDate: Date, tripDays: Array<Trip>) {
  const today = new Date();
  const isCurrentMonth = isSameYearAndMonth(today, selectedDate);
  const existTripDays = tripDays.filter((trip) => trip.tripCnt > 0);

  // 주행 기록이 없는 경우
  // 현재 월: 오늘 날짜에 포커스 / 과거 월: 해당 월의 1일에 포커스
  if (existTripDays.length === 0) {
    return isCurrentMonth ? today : selectedDate <= ccsAgreeDate ? ccsAgreeDate : startDateOfMonth(selectedDate);
  }

  // 주행기록이 있는 경우
  // 현재 월: 마지막 주행의 주 및 일자에 포커스 / 과거 월: 최초 주행의 주 및 일자에 포커스
  return setDay(selectedDate, isCurrentMonth ? existTripDays[existTripDays.length - 1].day : existTripDays[0].day);
}

export function isTripOnDay(tripDays: Array<Trip>, day: number) {
  return tripDays.some((trip) => trip.day === day && trip.tripCnt > 0);
}

export function getWeeksOfMonth(date: Date) {
  const monthStart = startDateOfMonth(date);
  const monthEnd = endDateOfMonth(date);

  return weekOfInterval(monthStart, monthEnd);
}

export function getWeekIndexOfDate(date: Date) {
  return getWeeksOfMonth(date).findIndex((weekStart) => isDateSameWeek(date, weekStart));
}

export default function useTripDayKr({vin, selectedDate}: {vin: string; selectedDate: Date}) {
  const today = new Date();
  const ccId = environmentStore.getState().ccid;
  const params = {
    vin,
    ccId,
    startDate: getYYYYMMFormat(subDate(today, {months: VALID_MONTH_PERIOD - 1})),
    endDate: getYYYYMMFormat(today),
  };

  const [{data: drivingData}, {data: tripDayData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.fuel.getMonthlyDrivingInfo), params],
        queryFn: () => diApi.fuel.getMonthlyDrivingInfo(params),
        select: ({data}: ApiResponseGetMonthlyDrivingInfoResponse) => data,
      },
      {
        queryKey: [getQueryKey(diApi.driveHistory.getTripDays1), vin, ccId, getYYYYMMFormat(selectedDate)],
        queryFn: () =>
          diApi.driveHistory.getTripDays1({
            vin,
            ccId,
            month: getYYYYMMFormat(selectedDate),
          }),
        select: ({data}: ApiResponseGetTripDaysResponse) => data,
      },
    ],
  });

  const verifiedCcsAgreeDate = parseDateWithFormat(drivingData.ccsAgreeDate);
  if (!verifiedCcsAgreeDate) {
    throw new Error('useTripDayKr ccsAgreeDate parse error');
  }
  const focusTripDate = getFocusTripDate(verifiedCcsAgreeDate, selectedDate, tripDayData.trips);

  return {
    tripDays: tripDayData.trips,

    verifiedCcsAgreeDate,
    focusTripDate,
  };
}
